import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Link,
  Tooltip
} from '@mui/material';
import { Add, Edit, LocationOn, FileUpload, FileDownload, Delete, HelpOutline } from '@mui/icons-material';
import { observer } from 'mobx-react';
import { SublocalityDto } from '../../../api-client';
import ModalSublocality from './ModalSublocality';
import GoogleMapsSublocalities from './GoogleMapsSublocalities';
import notificationStore from '../../../stores/Notification/notificationStore';
import { useAllSublocalities, useGetGeojsonSublocalities } from '../../../stores/Address/address.queries';
import { useDeleteSublocality, useImportGeojsonSublocalities } from '../../../stores/Address/address.mutations';
import WarningDialog from '../../../atoms/WarningDialog';

const SublocalitiesManager: React.FC = observer(() => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState<SublocalityDto | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<SublocalityDto | null>(null);
  const [importHelpOpen, setImportHelpOpen] = useState(false);

  // React Query hooks
  const { data: sublocalities = [], isLoading: loading, refetch: fetchSublocalities } = useAllSublocalities();
  const deleteSublocality = useDeleteSublocality();
  const importGeojson = useImportGeojsonSublocalities();
  const { refetch: exportGeojson } = useGetGeojsonSublocalities(false); // disable automatic fetch, only fetch on demand

  const handleEditRow = (row: SublocalityDto) => {
    setEditRow(row);
    setEditDialogOpen(true);
  };

  const handleCreateRow = () => {
    setEditRow(null);
    setEditDialogOpen(true);
  }

  const handleDeleteRow = (row: SublocalityDto) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteRow) return;
    deleteSublocality.mutate(deleteRow.id!, {
      onSettled: () => {
        setDeleteDialogOpen(false);
        setDeleteRow(null);
      },
    });
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteRow(null);
  };

  const handleCloseModal = () => {
    setEditDialogOpen(false);
    setEditRow(null); // Reset edit state to prevent unexpected behavior
  };

  const handleExportGeojson = async () => {
    try {
      const response = await exportGeojson();
      const geojsonObject = response.data;
      
      // Convert to formatted string for download 
      const geojsonStr = JSON.stringify(geojsonObject, null, 2);

      // Create and download the file
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD 
      const fileName = `sublocalities_${currentDate}.geojson`;
      const blob = new Blob([geojsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a'); 
      link.href = url;
      link.download = fileName || 'sublocalities.geojson';
      link.click();
      link.remove(); 
      URL.revokeObjectURL(url); 
      // console.log('GeoJSON exported successfully!');
    } catch (error) {
      console.error('Error exporting GeoJSON:', error);
      notificationStore.showMessage('Export fehlgeschlagen!', 'error');
    }
  };

  const handleImportGeojson = async () => {
    try {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.geojson,.json';
      
      // This to allow async operations when file is selected
      fileInput.onchange = async (event: Event) => {
        setActionLoading(true);
        const target = event.target as HTMLInputElement; 
        const file = target.files?.[0];
        if (!file) {
          setActionLoading(false);
          return;
        }
        try {
          const fileContent = await file.text();
          const geojsonData = JSON.parse(fileContent);

          // Basic validation 
          if (geojsonData.type !== 'FeatureCollection') {
            notificationStore.showMessage(`FeatureCollection erwartet, aber "${geojsonData.type}" erhalten.`, 'error');
            setActionLoading(false);
            return;
          }
          
          // Import the data using React Query mutation
          await importGeojson.mutateAsync(geojsonData);
        
          notificationStore.showMessage(`${geojsonData.features?.length || 0} Stadtteile erfolgreich importiert!`, 'success');
          setActionLoading(false);
        } catch (error) {
          console.error('Error importing GeoJSON:', error);
          notificationStore.showMessage('Import fehlgeschlagen!', 'error');
          setActionLoading(false);
        }
      
      }; 
      fileInput.click();
    } catch (error) {
      console.error('Error importing GeoJSON:', error);
      notificationStore.showMessage('Import fehlgeschlagen!', 'error');
      setActionLoading(false);
    }
  };

  return (
      <Box sx={{ width: '100%', p: 2 }}>
        {/* HEADER */}
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e0e0e0',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOn sx={{ mr: 1, fontSize: 28 }} />
            <Box>
              <Typography variant="h5">Stadtteile</Typography>
              <Typography variant="body2" color="text.secondary">
                Liste der vorhandenen Stadtteile
                </Typography>
            </Box>
          </Box>
          {/* Buttons section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* EXPORT GEOJSON BUTTON */}
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => handleExportGeojson()}
              startIcon={<FileUpload />}
              >
                Export Geojson
            </Button>
            
            {/* IMPORT GEOJSON BUTTON */}
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => handleImportGeojson()}
              startIcon={actionLoading ? <CircularProgress size={18} /> : <FileDownload />}
              disabled={actionLoading} 
            >
              {actionLoading ? 'Importing...' : 'Import Geojson'}
            </Button>

            {/* IMPORT INSTRUCTIONS */}
            <Tooltip title="Anleitung zum Import">
              <IconButton
                size="small"
                aria-label="Anleitung zum Geojson-Import"
                onClick={() => setImportHelpOpen(true)}
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>

            {/* ADD NEW SUBLOCALITY BUTTON */}
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => handleCreateRow()}
              startIcon={<Add />}    
              >
                Neuer Stadtteil
            </Button>
          </Box>
        </Box>

        {/* MAIN CONTENT - TABLE AND MAP */}
        <Grid container spacing={3}>
          {/* TABLE SECTION */}
              <Grid item xs={12} lg={6}>
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Abkürzung</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Farbe</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Aktionen</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : sublocalities.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">Keine Stadtteile gefunden</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        sublocalities.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.abbreviation || '-'}</TableCell>
                            <TableCell>
                              {row.color ? (
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    backgroundColor: row.color,
                                    border: '1px solid #ccc',
                                  }}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            {/* ACTIONS */}
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => handleEditRow(row)} sx={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                                <Edit />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteRow(row)} sx={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                                <Delete />
                              </IconButton>
                          </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
          </Grid>

          {/* GOOGLE MAPS SECTION */}
          <Grid item xs={12} lg={6}>
            <GoogleMapsSublocalities sublocalities={sublocalities} />
          </Grid>
        </Grid>
        {/* Add the modal dialog for adding/editing sublocalities */}
        {/* EDIT DIALOG */}
        <ModalSublocality
          editDialogOpen={editDialogOpen}
          editRow={editRow}
          sublocalities={sublocalities}
          onClose={handleCloseModal}
        />

        {/* DELETE CONFIRMATION DIALOG */}
        <WarningDialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          onContinue={handleConfirmDelete}
          title="Stadtteil löschen"
          content={<>Sind Sie sicher, dass Sie <strong>"{deleteRow?.name}"</strong> löschen möchten?</>}
          loading={deleteSublocality.isPending}
        />

        {/* IMPORT INSTRUCTIONS DIALOG */}
        <Dialog
          open={importHelpOpen}
          onClose={() => setImportHelpOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Stadtteile als Geojson importieren</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.5 } }}>
                <li>
                  <Link
                    href="https://geojson.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    geojson.io
                  </Link>{' '}
                  öffnen.
                </li>
                <li>
                  Mit dem <strong>Polygon-Werkzeug</strong> ("Draw Polygon") das Gebiet
                  eines Stadtteils einzeichnen.
                </li>
                <li>
                  Im Eigenschaften-Fenster des Polygons die Farbe festlegen. geojson.io
                  speichert sie als <code>fill</code> — dieses Feld wird beim Import als
                  Farbe des Stadtteils übernommen.
                </li>
                <li>
                  <strong>Wichtig:</strong> Name und Abkürzung setzt geojson.io nicht
                  automatisch. Für jedes Polygon müssen die Eigenschaften{' '}
                  <code>name</code> und <code>abbreviation</code> von Hand ergänzt
                  werden, sonst wird der Stadtteil ohne Namen importiert.
                </li>
                <li>
                  Alle Polygone als <code>.geojson</code>-Datei speichern und hier über{' '}
                  <strong>Import Geojson</strong> hochladen.
                </li>
              </Box>
              <Box sx={{ mt: 2 }}>
                Groß- und Kleinschreibung der Eigenschaften spielt keine Rolle
                (<code>Name</code> wie <code>name</code>), und statt <code>fill</code>{' '}
                wird auch <code>color</code> akzeptiert.
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportHelpOpen(false)}>Schließen</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  });

  export default SublocalitiesManager;